import React, { useState, useMemo } from 'react';
import { Upload, Download, RefreshCcw } from 'lucide-react';
import FrameRow from './FrameRow';
import FrameRowArray from './FrameRowArray';
import FrameCanvas from './FrameCanvas';
import FrameRowSelect from './FrameRowSelect';
import './FrameDesign.css';

const initialData = [
  { id: 0, descr: 'Screen Name', sect: 'screen', value: '220011-Knowhere' },
  { id: 1, descr: 'Screen Radius (mm)', sect: 'screen', value: 30000 },
  { id: 2, descr: 'Lower Chord (mm)', sect: 'screen', value: 19000 },
  { id: 3, descr: 'Upper Chord (mm)', sect: 'screen', value: 18600 },
  { id: 4, descr: 'Screen Height (mm)', sect: 'screen', value: 11335 },
  { id: 5, descr: 'Smile (mm)', sect: 'screen', value: 590 },
  { id: 6, descr: 'Frown (mm)', sect: 'screen', value: 100 },
  { id: 7, descr: 'Distance to Floor (mm)', sect: 'screen', value: 300 },
  { id: 8, descr: 'Bays', sect: 'screen', value: 7 },
  { id: 9, descr: 'Shaker Rows', sect: 'screen', value: 4 },
  { id: 10, descr: 'Shaker Offset', sect: 'screen', value: 0 },
  { id: 11, descr: 'Layers', sect: 'screen', value: '2, 2, 1, 2, 2, 0.5' },
];

const FrameDesigner = () => {
  const [items, setItems] = useState(initialData);
  // const [title, setTitle] = useState('');

  const handleTitleChange = (event) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === 0 ? { ...item, ['value']: event.target.value } : item,
      ),
    );
  };

  const handleInputChange = (id, field, value) => {
    let numericValue = parseFloat(value) || 0;
    // numericValue = Math.max(0, numericValue);

    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, [field]: numericValue } : item,
      ),
    );
  };

  const handleTextChange = (id, field, value) => {
    // Ensure input is a non-negative integer or float
    let textValue = value;

    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, [field]: textValue } : item,
      ),
    );
  };

  // Resets to initial data
  const resetData = () => {
    setItems(initialData);
    // setTitle('');
  };

  const checkBays = (loArc) => {
    const bayArcLimits = [
      { bays: '13', arc: 31300 },
      { bays: '11+', arc: 28800 },
      { bays: '11', arc: 26500 },
      { bays: '9+', arc: 24100 },
      { bays: '9', arc: 21600 },
      { bays: '7+', arc: 19200 },
      { bays: '7', arc: 16800 },
      { bays: '5+', arc: 14500 },
      { bays: '5', arc: 12120 },
      { bays: 'na', arc: 0 },
    ];

    return bayArcLimits.find((i) => i.arc <= loArc).bays;
  };

  //12000	14508	20311	26115

  const checkSect = (loArc) => {
    const sectArcLimits = [
      { sects: '11', arc: 26115 },
      { sects: '9', arc: 20311 },
      { sects: '7', arc: 14508 },
      { sects: '5', arc: 12000 },
      { sects: 'na', arc: 0 },
    ];

    return sectArcLimits.find((i) => i.arc <= loArc).sects;
  };

  const estLayers = (bpl, ht) => {
    const spkrBot = bpl + 0.5 * ht - 585;
    const firstLayer = 150;
    const spkrLayer = 2000;
    const topLayer = 1600;

    const lowLayers = [];
    let remainder = spkrBot - firstLayer;
    while (remainder >= 500) {
      if (remainder >= 2000) {
        remainder -= 2000;
        lowLayers.push(2000);
      } else if (remainder >= 1000) {
        remainder -= 1000;
        lowLayers.push(1000);
      } else if (remainder >= 500) {
        remainder -= 500;
        lowLayers.push(500);
      }
    }

    const totLowLayers = lowLayers.reduce((total, val) => total + val, 0);

    const hiLayers = [spkrLayer];
    remainder = bpl + ht - totLowLayers - spkrLayer - topLayer - firstLayer;
    while (remainder >= 500) {
      if (remainder >= 2000) {
        remainder -= 2000;
        hiLayers.push(2000);
      } else if (remainder >= 1000) {
        remainder -= 1000;
        hiLayers.push(1000);
      } else if (remainder >= 500) {
        remainder -= 500;
        hiLayers.push(500);
      }
    }

    return lowLayers.concat(hiLayers).map((num) => num / 1000.0);
  };

  const summary = useMemo(() => {
    const loChord =
      items.find((i) => i.descr === 'Lower Chord (mm)').value || 15000;
    const upChord =
      items.find((i) => i.descr === 'Upper Chord (mm)').value || 14000;
    let rad =
      items.find((i) => i.descr === 'Screen Radius (mm)').value || 25000;
    if (rad < 25000) rad = 25000;
    const lowArc = 2 * rad * Math.asin((0.5 * loChord) / rad);
    const upArc = 2 * rad * Math.asin((0.5 * upChord) / rad);

    const bays = checkBays(lowArc);
    const sects = checkSect(lowArc);

    const bpl =
      items.find((i) => i.descr === 'Distance to Floor (mm)').value || 300;
    const ht =
      items.find((i) => i.descr === 'Screen Height (mm)').value || 10000;
    const layers = estLayers(bpl, ht);
    // console.log(layers);

    return {
      lowArc,
      upArc,
      bays,
      sects,
      layers,
    };
  }, [items]);

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          setItems(JSON.parse(e.target.result));
        } catch (error) {
          console.error('Error parsing JSON:', error);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleDownload = () => {
    const filename = items[0].value + '.json';
    const jsonData = JSON.stringify(items, null, 2);

    // Create a Blob and download link
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);

    // Append to body, click, and remove for download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up the URL object
  };

  return (
    <>
      <div className="content-wrapper">
        <header className="app-header">
          <h1 className="header-title">Frame Design</h1>

          <div className="button-group">
            <label htmlFor="csv-upload" className="action-button load-button">
              <Upload size={16} />
              <span>Load</span>
            </label>
            <input
              type="file"
              id="csv-upload"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleUpload}
              onClick={(e) => {
                e.target.value = null;
              }}
            />
            <button
              onClick={handleDownload}
              className="action-button download-button"
              aria-label="Save data"
            >
              <Download size={16} />
              <span>Save</span>
            </button>
            <button
              onClick={resetData}
              className="action-button reset-button"
              aria-label="Reset to initial data"
            >
              <RefreshCcw size={16} />
              <span>Reset</span>
            </button>
          </div>
        </header>
        <div>
          <input
            type="text"
            id="titleInput"
            className="title-input"
            placeholder="Enter the SAP and Name"
            value={items[0].value}
            onChange={handleTitleChange}
          />
        </div>
        <main>
          <div className="data-table">
            <div className="titleRow">Screen Parameters</div>
            <FrameRow onChange={handleInputChange} item={items[1]} />
            <FrameRow onChange={handleInputChange} item={items[2]} />
            <FrameRow onChange={handleInputChange} item={items[3]} />
            <FrameRow onChange={handleInputChange} item={items[4]} />
            <FrameRow onChange={handleInputChange} item={items[5]} />
            <FrameRow onChange={handleInputChange} item={items[6]} />
            <FrameRow onChange={handleInputChange} item={items[7]} />
            <FrameRowSelect onChange={handleTextChange} item={items[8]} />
            <FrameRow onChange={handleInputChange} item={items[9]} />
            <FrameRow onChange={handleInputChange} item={items[10]} />
            <FrameRowArray onChange={handleTextChange} item={items[11]} />
            <div className="titleRow">Analysis</div>
            <div className="loadRow">
              <div>Lower Arc Length (mm):</div>
              <div>{summary.lowArc.toFixed(0)}</div>
            </div>
            <div className="loadRow">
              <div>Upper Arc Length (mm): </div>
              <div>{summary.upArc.toFixed(0)}</div>
            </div>
            <div className="loadRow">
              <div>Recommended Bays: </div>
              <div>{summary.bays}</div>
            </div>

            <div className="loadRow">
              <div>Recommended Frame Sect:</div>
              <div>{summary.sects}</div>
            </div>

            <div>Recommended Layers: </div>
            <div className="arrayBox">{summary.layers.join(', ')}</div>
          </div>
          <div className="graphics">
            <FrameCanvas params={items} />
          </div>
        </main>
      </div>
    </>
  );
};

export default FrameDesigner;
